
import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
} from "@mui/material";
import { axiosInstance } from "src/api/config/axios";
import config from "../apiServices/appConfig";


const { BASE_URL, API_VERSION } = config;

interface HyperVergeKYBProps {
  clientId?: string; 
  onStatusChange?: (status: string, data: any) => void;
}

const WORKFLOW_ID = "E7EkTB_18_06_25_14_57_13";
const APP_ID = "4uy459"; // Staging
const APP_KEY = "s2bxcqs0qwruuffhhjo6";

// Function to POST KYB result
async function postKybResult({ transactionId, appId }: { transactionId: string, appId: string }) {
  console.log(transactionId, "from hyperverse")
  try {
    const url = `${BASE_URL}${API_VERSION}/kyb/create-kyb`;
    const body = {
      transactionId,
      sendFlag: "yes"
    };
    const token = localStorage.getItem("access_token");
    const headers = {
      appId,
      transactionId,
      appkey: APP_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
    const response = await axiosInstance.post(url, body, { headers });
    return response.data;
  } catch (error) {
    // Optionally handle/log error
    console.error("Failed to POST KYB result:", error);
    return null;
  }
}

// Utility to get clientId from localStorage
function getClientIdFromLocalStorage(): string | null {
  try {
    const authResponseStr = localStorage.getItem('auth_response');
    if (authResponseStr) {
      const authResponse = JSON.parse(authResponseStr);
      if (authResponse && authResponse.clientDetails && authResponse.clientDetails.client_id) {
        return authResponse.clientDetails.client_id;
      }
    }
  } catch (e) {
    // ignore
  }
  return null;
}

const HyperVergeKYB: React.FC<HyperVergeKYBProps> = ({
  onStatusChange,
}) => {
  const [started, setStarted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  // Get clientId from localStorage if not provided as prop
  const effectiveClientId = getClientIdFromLocalStorage();

  const fetchAccessToken = async () => {
    setLoading(true);
    setError(null);
    setStatus(null);
    setAccessToken(null);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axiosInstance.get(
        `${BASE_URL}${API_VERSION}/kyb/generate-access-token`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const accessToken = response.data?.token || response.data?.token;
      if (!accessToken) throw new Error("No access token received");
      setAccessToken(accessToken);
      return accessToken;
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to get access token"
      );
      setLoading(false);
      return null;
    }
  };


  // Step 2: Start HyperVerge KYB process
  const startHyperVerge = (token: string) => {
    const win = window as any;
    if (!win.HyperKYCModule || !win.HyperKycConfig) {
      setError(
        "HyperVerge SDK not loaded. Please ensure the SDK script is included in index.html."
      );
      setLoading(false);
      return;
    }
    // Prefetch (optional)
    if (typeof win.HyperKYCModule.prefetch === "function") {
      try {
        win.HyperKYCModule.prefetch({ appId: APP_ID, workflowId: WORKFLOW_ID });
      } catch (e) {
        console.warn("Prefetch failed or not supported:", e);
      }
    }
    // Create config
    const config = new win.HyperKycConfig(
      token,
      WORKFLOW_ID,
      effectiveClientId
    );
    // Handler
    const handler = async (result: any) => {
      setLoading(false);
      setStatus(result.status);
      if (onStatusChange) {
        onStatusChange(result.status, result);
      }
      const transactionId = effectiveClientId || result.transactionId || "";
      switch (result.status) {
        case "user_cancelled":
          setError("You cancelled the KYB process.");
          await postKybResult({ transactionId, appId: APP_ID });
          break;
        case "error":
          setError(result.error || "An error occurred during KYB.");
          await postKybResult({ transactionId, appId: APP_ID });
          break;
        case "auto_approved":
          setStatus("Your KYB was auto-approved!");
          await postKybResult({ transactionId, appId: APP_ID });
          break;
        case "auto_declined":
          setError("Your KYB was auto-declined.");
          await postKybResult({ transactionId, appId: APP_ID });
          break;
        case "needs_review":
          setStatus("Your KYB requires manual review.");
          await postKybResult({ transactionId, appId: APP_ID });
          break;
        default:
          setStatus("Unknown status: " + result.status);
          await postKybResult({ transactionId, appId: APP_ID });
      }
    };

    if (typeof win.HyperKYCModule.launch === "function") {
      win.HyperKYCModule.launch(config, handler);
    } else {
      setError("HyperVerge SDK launch function not available.");
      setLoading(false);
    }
  };

  const handleStart = async () => {
    setStarted(true);
    setLoading(true);
    setError(null);
    setStatus(null);
    // 1. Get access token
    const token = await fetchAccessToken();
    if (token) {
      // 2. Start HyperVerge
      startHyperVerge(token);
    }
    // If token fetch fails, loading is already set to false in fetchAccessToken
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight={300}
      px={2}
      sx={{
        width:'100%',
       
      }}

    
    >
      <Typography
        variant="h6"
        gutterBottom
        align="center"
        sx={{
          mb: 6,
          fontWeight: "bold",
        }}
      >
        Complete your KYB verification with HyperVerge
      </Typography>

      {!started && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleStart}
          disabled={loading}
        >
          Start KYB Verification
        </Button>
      )}

      {loading && (
        <Box mt={3} display="flex" alignItems="center">
          <CircularProgress size={24} />
          <Typography ml={2}>Processing...</Typography>
        </Box>
      )}

      {status && !loading && !error && (
        <Alert severity="success" sx={{ mt: 3 }}>
          {status}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default HyperVergeKYB;
